import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { DefaultWeight, DefaultWeightProps } from '@/domain/models/DefaultWeight'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'

import {
  DefaultWeightDTO,
  IDefaultWeightsRepository,
  RequestGetDefaultWeightsResponse
} from './IDefaultWeightsRepository'

import { NEXATLAS_API_BASE } from '@/infra/http/apiBaseUrls'
import type { IHttpClient } from '@/infra/http/IHttpClient'

@injectable()
export class DefaultWeightsRepository implements IDefaultWeightsRepository {
  private defaultWeights: DefaultWeight[] = []

  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState
  ) {}

  protected getRemoteUrl(): string {
    return `${NEXATLAS_API_BASE}/wbStandardWeight`
  }

  async init(): Promise<Result<void>> {
    try {
      const result = await this.getAll()
      if (result.isFailure) return Result.fail(result.error)
      return Result.ok()
    } catch (error) {
      return Result.fail(error as Error)
    }
  }

  async getAll(): Promise<Result<DefaultWeight[]>> {
    try {
      if (this.defaultWeights?.length > 0) {
        return Result.ok(this.defaultWeights)
      }
      const response = await this.httpClient.get<RequestGetDefaultWeightsResponse[]>(`${this.getRemoteUrl()}`, {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        }
      })

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      const weights = response.response.map((dto) => this._remoteToLocal(dto)).filter((dw) => !dw.deletedAt)
      this.defaultWeights = weights
      return Result.ok(weights)
    } catch (error) {
      console.error('Error in getAllDefaultWeights:', error)
      return Result.fail(error as Error)
    }
  }

  async getById(id: string): Promise<Result<DefaultWeight | null>> {
    try {
      const found = this.defaultWeights?.find((dw) => dw.id?.toString() === id)
      if (found) {
        return Result.ok(found)
      }
      const response = await this.httpClient.get<RequestGetDefaultWeightsResponse>(`${this.getRemoteUrl()}/${id}`, {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        }
      })

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      return Result.ok(this._remoteToLocal(response.response))
    } catch (error) {
      console.error('Error in getDefaultWeightById:', error)
      return Result.fail(error as Error)
    }
  }

  async upsert(defaultWeight: DefaultWeight): Promise<Result<void>> {
    try {
      const updatedDefaultWeight = DefaultWeight.create(defaultWeight.props, defaultWeight.id)
      const dto = this._localToRemote(updatedDefaultWeight)

      const response = await this.httpClient.post(`${this.getRemoteUrl()}`, dto, {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        }
      })

      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      this.defaultWeights = this.defaultWeights.filter((dw) => dw.id?.toString() !== defaultWeight.id?.toString())
      this.defaultWeights.push(defaultWeight)
      return Result.ok()
    } catch (error) {
      console.error('Error in upsertDefaultWeight:', error)
      return Result.fail(error as Error)
    }
  }

  async delete(id: string): Promise<Result<void>> {
    try {
      const response = await this.httpClient.delete(
        `${this.getRemoteUrl()}/${id}`,
        {},
        {
          headers: {
            Authorization: `Token ${this.authState.getStateSnapshot().token}`
          }
        }
      )
      if (response.success === false || response.status === 500) {
        return Result.fail((response as any).error || 'Server error')
      } else if (response.status < 200 || response.status >= 300) {
        return Result.fail((response as any).message || 'Request failed')
      }

      this.defaultWeights = this.defaultWeights.filter((dw) => dw.id?.toString() !== id)
      return Result.ok()
    } catch (error) {
      console.error('Error in deleteDefaultWeight:', error)
      return Result.fail(error as Error)
    }
  }

  // Private methods

  private _remoteToLocal(dto: RequestGetDefaultWeightsResponse): DefaultWeight {
    return DefaultWeight.create(
      {
        name: dto.name,
        weight: dto.weight,
        weightUnit: dto.weightUnit,
        stationType: dto.stationType,
        createdAt: new Date(dto.createdAt),
        updatedAt: new Date(dto.updatedAt)
      },
      dto.id ? new UniqueEntityID(dto.id) : undefined
    )
  }

  private _localToRemote(defaultWeight: DefaultWeight): DefaultWeightDTO {
    return {
      id: defaultWeight.id ? defaultWeight.id.toString() : new UniqueEntityID().toString(),
      name: defaultWeight.name,
      weight: defaultWeight.weight,
      weightUnit: defaultWeight.weightUnit,
      stationType: defaultWeight.stationType,
      updatedAt: defaultWeight.updatedAt.toISOString()
    } as DefaultWeightDTO
  }
}
